import React, { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { ApiTypes } from 'common';
import { UserSettings } from '@src/pages/Login/UserSettings';
import { Pathname, Browsing } from '@src/types/Browsing';
import { Services } from '@src/services';
import { ServiceName } from '@src/types/Services';
import { Login } from '@src/types/Login';
import { LocalizedStrings } from '@src/utils/localizedStrings';
import { style, classes, media, stylesheet } from 'typestyle';
import { ADALONG_COLORS } from '@src/styles';
import { userAtom } from '@src/state/userAtom';
import { Column, Row } from '../Utils';
import { SelectableButton, SelectableButtonStyle } from '../SelectableButton';
import decathlonLogo from '../../assets/img/site/decathlon-logo-white.png';
import logo from '../../assets/img/site/logo_alone_white.png';

interface Props {
  active: Pathname;
}

interface NavItem {
  pathname?: Pathname;
  label: string;
  featureKey?: keyof ApiTypes.ResGetMe['user']['groupFeatures'];
  href?: string;
}

const lang = LocalizedStrings({
  en: {
    library: 'Discover',
    collections: 'Collect',
    analyze: 'Analyze',
    crm: 'CRM',
    share: 'Share',
    upload: 'Upload',
    widgets: 'Widget',
    product: 'Products',
    premiumFeatureWarning: 'This is a premium feature. Please contact us for more information.',
  },
});

const NAV_ITEMS: NavItem[] = [
  { pathname: Pathname.discover, label: lang.library },
  { pathname: Pathname.product, label: lang.product, featureKey: 'product_catalog' },
  { pathname: Pathname.collect, label: lang.collections },
  { pathname: Pathname.share, label: lang.share, featureKey: 'share' },
  { pathname: Pathname.widget, label: lang.widgets, featureKey: 'widget' },
  { pathname: Pathname.analyze, label: lang.analyze },
  { pathname: Pathname.crm, label: lang.crm, featureKey: 'crm' },
  { label: lang.upload, featureKey: 'upload_ba', href: 'https://upload-ugc.adalong.com/' },
];

function NavBar({ active }: Props) {
  const user = useAtomValue(userAtom);

  const browsingService = useMemo(() => Services.get<Browsing>(ServiceName.Browsing), []);
  const loginService = useMemo(() => Services.get<Login>(ServiceName.Login), []);

  if (!user?.email || !user?.group?.name) {
    return null;
  }

  const isDecathlonUser =
    user.email.toLowerCase().includes('@decathlon') ||
    user.group.name.toLowerCase().includes('decathlon');

  const isDisabled = (featureKey?: keyof ApiTypes.ResGetMe['user']['groupFeatures']) =>
    featureKey ? !user.groupFeatures[featureKey] : false;

  return (
    <nav className={sheet.navbar}>
      <div className={sheet.logo} onClick={() => browsingService.goToPage(Pathname.discover)}>
        {isDecathlonUser ? (
          <>
            <img src={decathlonLogo} className={sheet.decathlonLogoImg} />
            <span className={sheet.decathlonMessage}>SMART CONTENT NETWORK</span>
          </>
        ) : (
          <img src={logo} className={sheet.logoImg} />
        )}
      </div>

      <Column className={sheet.verticalButtonsBar}>
        <Row className={classes(sheet.navButtons, sheet.buttonsBar)}>
          {NAV_ITEMS.map(({ pathname, label, featureKey, href }) => {
            const disabled = isDisabled(featureKey);
            return (
              <SelectableButton
                key={pathname ?? label}
                classes={disabled ? [buttonClass, sheet.disabled] : [buttonClass]}
                selected={pathname ? active === pathname : false}
                buttonStyle={buttonStyle}
                onClick={() => {
                  if (disabled) {
                    return;
                  }

                  if (href) {
                    window.open(href, '_blank', 'noopener,noreferrer');
                    return;
                  }

                  if (pathname) {
                    browsingService.goToPage(pathname);
                  }
                }}
                disable={disabled ? lang.premiumFeatureWarning : undefined}
              >
                {label.toUpperCase()}
              </SelectableButton>
            );
          })}
        </Row>
      </Column>

      <Column className={sheet.accountButton}>
        <UserSettings
          user={user}
          onLogout={() => loginService.logout()}
          onSwitchGroup={(groupId) => loginService.switchGroup(groupId)}
        />
      </Column>
    </nav>
  );
}

const sheet = stylesheet({
  navbar: {
    paddingTop: 25,
    paddingBottom: 10,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    $nest: {
      img: {
        cursor: 'pointer',
      },
      '@media (max-width: 600px)': {
        alignItems: 'center',
        flexWrap: 'wrap',
        padding: '10px 12px 8px',
      },
    },
  },
  navButtons: {
    flexDirection: 'row',
    fontSize: 14,
    float: 'none',
    $nest: {
      '@media (max-width: 600px)': {
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: 4,
        width: '100%',
      },
    },
  },
  decathlonMessage: {
    marginTop: 'auto',
    fontFamily: '\'Roboto Condensed\'',
    fontWeight: 'bold',
    paddingLeft: 20,
    display: 'inline-block',
    position: 'relative',
    top: '50%',
    transform: 'translate(0px, -50%)',
    verticalAlign: 'top',
    fontSize: '1rem',
    height: '0.75rem',
    lineHeight: '0.8rem',
  },
  logo: {
    paddingLeft: 13,
    $nest: {
      '@media (max-width: 600px)': {
        paddingLeft: 0,
      },
    },
  },
  logoImg: {
    height: 60,
    $nest: {
      '@media (max-width: 600px)': {
        height: 42,
      },
    },
  },
  decathlonLogoImg: {
    width: 90,
    margin: 12,
  },
  disabled: {
    color: 'gray !important',
  },
  buttonsBar: {
    justifyContent: 'center',
    paddingLeft: 0,
    marginBottom: 0,
    listStyle: 'none',
    flexGrow: 0,
  },
  verticalButtonsBar: {
    justifyContent: 'center',
    flexGrow: 1,
    flexShrink: 1,
    $nest: {
      '@media (max-width: 600px)': {
        order: 3,
        flex: '0 0 100%',
        width: '100%',
        marginTop: 8,
      },
    },
  },
  accountButton: {
    marginRight: 18,
    justifyContent: 'center',
    $nest: {
      '@media (max-width: 600px)': {
        order: 2,
        marginRight: 0,
      },
    },
    textAlign: 'right',
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 'auto',
  },
});

const buttonClass = style({
  padding: '0 22px',
  fontWeight: 'bold',
}, media({
  maxWidth: 950,
}, {
  padding: '0 12px',
}), media({
  maxWidth: 800,
}, {
  padding: '0 4px',
}));

const buttonStyle: SelectableButtonStyle = {
  size: 14,
  borderColor: '#0041fa',
  textColor: ADALONG_COLORS.LIGHT_WHITE,
  borderSize: 3,
};

export default NavBar;
